import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useDispatch, useSelector } from 'react-redux';
import type { RootState } from '../redux/store';
import {
	setTags,
	setUserSubscriptions,
} from '../redux/slices/profile.slice';
import {
	addAnnualDeclarations,
	setDeclarationsOverview,
	setIsLoadingAnnualDeclarations,
	setIsLoadingDeclarations,
	setIsLoadingTaxpayer,
	setRetentionCertificates,
	setRetentionCertificatesMeta,
	setTaxpayer,
} from '../redux/slices/taxProfile.slice';
import { addAcquisitions } from '../redux/slices/products.slice';
import {
	setAllOrders,
	setIsLoadingOrderCheckout,
	setOrderCheckout,
} from '../redux/slices/cart.slice';
import { setIsLoadingBasicInformation } from '../redux/slices/ui.slice';
import { showAlert } from '../redux/slices/alert.slice';
import { ALERT_TYPE } from '../components/molecules/Alert';
import { getTaxpayerService } from '../shared/services/fiscal.service';
import { getUserTagsService } from '../shared/services/tagging.service';
import {
	deleteCartShoppingItems,
	getOrderRefresh,
} from '../services/sales.service';
import { fetchSalesOverview } from '../services/salesOverview.service';
import { DECLARATIONS_NEW_QUERY_KEYS, declarationsNewOverviewQuery } from '../features/declarations-new/hooks/overviewQuery';
import { adaptNewOverviewToV2 } from '../features/declarations-new/lib/adaptNewOverviewToV2';
import { fetchMyCertificatesService } from '../shared/services/bussiness.service';
import type {
	RappiCertificate,
	RetentionCertificatesMeta,
} from '../@types/FiscalDocuments.type';
import { filterExpiredShoppingItems } from '../shared/constants/expiringProducts';
import {
	EXPERIMENT_ASSIGNMENTS_QUERY_KEY,
	useExperimentAssignments,
} from '../shared/experiments/useExperimentAssignments';

const BOOTSTRAP_QUERY_KEYS = {
	taxpayer: ['bootstrap', 'taxpayer'] as const,
	// One combined query for subscriptions + acquisitions (heru-sales-transaction
	// -engine /user/sales-overview). Replaces the former `subscriptions` and
	// `acquisitions` keys — a single fetch feeds both Redux slices.
	salesOverview: ['bootstrap', 'salesOverview'] as const,
	tags: ['bootstrap', 'tags'] as const,
	declarations: DECLARATIONS_NEW_QUERY_KEYS.overview,
	openOrder: ['bootstrap', 'openOrder'] as const,
	experiments: EXPERIMENT_ASSIGNMENTS_QUERY_KEY,
	retentionCerts: (userId: number) =>
		['bootstrap', 'retentionCerts', userId] as const,
};

type RetentionCertificatesResult = {
	certificates: RappiCertificate[];
	meta: RetentionCertificatesMeta;
};

const fetchOpenOrder = async () => {
	const params = '?status=open&status=failed&is_renewal=false';
	const orders = await getOrderRefresh(params);
	const first = orders?.[0];
	const expiredItems = filterExpiredShoppingItems(first?.shopping_item);
	if (expiredItems.length && first?.id) {
		const itemIds = expiredItems.map((item: { id: number }) => ({
			shopping_item_id: item.id,
		}));
		try {
			await deleteCartShoppingItems(itemIds, first.id);
			return await getOrderRefresh(params);
		} catch {
			return orders;
		}
	}
	return orders;
};

/**
 * GET /certificates/me resuelve la identidad en servidor (RFC + correo + teléfono),
 * así que no exige RFC capturado y trae Rappi y Zubale en una sola llamada. Un 404
 * del backend (FEATURE_CERTIFICATES_ME apagado) se trata como "sin certificados"
 * para no romper el arranque del app.
 */
const fetchMyCertificates = async (): Promise<RetentionCertificatesResult> => {
	try {
		const response = await fetchMyCertificatesService({ limit: 100 });
		const certificates = response?.data ?? [];
		return {
			certificates,
			meta: {
				source: 'me',
				rfc_linked: response?.identity?.rfc_linked ?? false,
				matched_by: response?.identity?.matched_by ?? {
					rfc: 0,
					email: 0,
					phone: 0,
				},
				total: response?.total ?? certificates.length,
			},
		};
	} catch (error: any) {
		if (error?.response?.status === 404) {
			return {
				certificates: [],
				meta: {
					source: 'me',
					rfc_linked: false,
					matched_by: { rfc: 0, email: 0, phone: 0 },
					total: 0,
				},
			};
		}
		throw error;
	}
};

export const useBootstrapQueries = () => {
	const dispatch = useDispatch();
	const userProfile = useSelector((state: RootState) => state.profile.user);

	const userId = userProfile?.id;
	const enabledBase = !!userId;

	const taxpayerQuery = useQuery({
		queryKey: BOOTSTRAP_QUERY_KEYS.taxpayer,
		queryFn: async () => {
			try {
				return await getTaxpayerService();
			} catch (error) {
				if ((error as { status?: number })?.status === 404) return null;
				throw error;
			}
		},
		enabled: enabledBase,
		staleTime: 5 * 60 * 1000,
	});

	// Single combined query: one network call feeds both subscriptions and
	// acquisitions. The backend resolves the user from the JWT, so no token is
	// passed (FetchV2 attaches it under the hood).
	const salesOverviewQuery = useQuery({
		queryKey: BOOTSTRAP_QUERY_KEYS.salesOverview,
		queryFn: () => fetchSalesOverview(),
		enabled: enabledBase,
		staleTime: 5 * 60 * 1000,
	});

	const tagsQuery = useQuery({
		queryKey: BOOTSTRAP_QUERY_KEYS.tags,
		queryFn: getUserTagsService,
		enabled: enabledBase,
		staleTime: 5 * 60 * 1000,
	});

	const declarationsQuery = useQuery({
		...declarationsNewOverviewQuery,
		refetchInterval: false,
		select: adaptNewOverviewToV2,
		enabled: enabledBase,
	});

	const openOrderQuery = useQuery({
		queryKey: BOOTSTRAP_QUERY_KEYS.openOrder,
		queryFn: fetchOpenOrder,
		enabled: enabledBase,
		staleTime: 30 * 1000,
	});

	useExperimentAssignments();

	const retentionCertsQuery = useQuery({
		queryKey: BOOTSTRAP_QUERY_KEYS.retentionCerts(userId ?? 0),
		queryFn: fetchMyCertificates,
		enabled: enabledBase,
		staleTime: 5 * 60 * 1000,
	});

	useEffect(() => {
		dispatch(setIsLoadingTaxpayer(taxpayerQuery.isPending));
	}, [taxpayerQuery.isPending, dispatch]);

	useEffect(() => {
		if (taxpayerQuery.data !== undefined) {
			dispatch(setTaxpayer(taxpayerQuery.data));
		}
	}, [taxpayerQuery.data, dispatch]);

	useEffect(() => {
		if (taxpayerQuery.isError) {
			dispatch(
				showAlert({
					type: ALERT_TYPE.WARNING,
					title: 'Aviso',
					description: 'Ocurrió un error al obtener la información fiscal.',
				}),
			);
		}
	}, [taxpayerQuery.isError, dispatch]);

	useEffect(() => {
		if (salesOverviewQuery.data !== undefined) {
			const { subscriptions, acquisitions, errors } = salesOverviewQuery.data;
			if (!errors.subscriptions) dispatch(setUserSubscriptions(subscriptions));
			if (!errors.acquisitions) dispatch(addAcquisitions(acquisitions));
		}
	}, [salesOverviewQuery.data, dispatch]);

	useEffect(() => {
		if (tagsQuery.data !== undefined) {
			dispatch(setTags(tagsQuery.data));
		}
	}, [tagsQuery.data, dispatch]);

	useEffect(() => {
		dispatch(setIsLoadingDeclarations(declarationsQuery.isPending));
		dispatch(setIsLoadingAnnualDeclarations(declarationsQuery.isPending));
	}, [declarationsQuery.isPending, dispatch]);

	useEffect(() => {
		if (!declarationsQuery.data) return;
		const yearDataList = declarationsQuery.data.data;
		dispatch(setDeclarationsOverview(yearDataList.flatMap((yearData) => yearData.monthly)));
		dispatch(addAnnualDeclarations(yearDataList.flatMap((yearData) => (yearData.annual ? [yearData.annual] : []))));
	}, [declarationsQuery.data, dispatch]);

	useEffect(() => {
		dispatch(setIsLoadingOrderCheckout(openOrderQuery.isPending));
	}, [openOrderQuery.isPending, dispatch]);

	useEffect(() => {
		if (openOrderQuery.data !== undefined) {
			const orders = openOrderQuery.data ?? [];
			dispatch(setAllOrders(orders));
			dispatch(setOrderCheckout(orders?.[0] ?? null));
		}
	}, [openOrderQuery.data, dispatch]);

	useEffect(() => {
		if (retentionCertsQuery.data !== undefined) {
			dispatch(setRetentionCertificates(retentionCertsQuery.data.certificates));
			dispatch(setRetentionCertificatesMeta(retentionCertsQuery.data.meta));
		}
	}, [retentionCertsQuery.data, dispatch]);

	const criticalSettled =
		!enabledBase ||
		(!taxpayerQuery.isPending &&
			!salesOverviewQuery.isPending &&
			!tagsQuery.isPending);

	useEffect(() => {
		if (criticalSettled) {
			dispatch(setIsLoadingBasicInformation(false));
		}
	}, [criticalSettled, dispatch]);

	return { isLoading: !criticalSettled };
};

export { BOOTSTRAP_QUERY_KEYS };
